import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { Impound } from '../lib/calculations';
import { useHybridAuth } from './useHybridAuth';

/**
 * When a call's vehicle was impounded and released
 * (GET /impounds/by-dispatch/:id), to count the days of storage an invoice
 * bills. Null for a call with no impound, and for a member who only drives,
 * who is not sent the dates: days held from no date is NaN, so they bill as
 * a call without one.
 */
export const useCallImpound = (
  callId: string | undefined,
  companyId: string | undefined,
  config?: { enabled?: boolean }
) => {
  const { getAccessTokenSilently, isAuthenticated } = useHybridAuth();

  return useQuery({
    queryKey: ['call-impound', callId, companyId],
    queryFn: async (): Promise<Impound | null> => {
      const token = await getAccessTokenSilently();
      try {
        const { data } = await api.get(`/impounds/by-dispatch/${callId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'x-company-id': companyId!,
          },
        });
        return data?.impoundDate
          ? {
              impoundDate: data.impoundDate,
              releasedDate: data.releasedDate || null,
            }
          : null;
      } catch {
        // No impound for the call; it bills a day of storage.
        return null;
      }
    },
    enabled:
      config?.enabled !== false && isAuthenticated && !!callId && !!companyId,
  });
};
